/**
 * 富文本片段模型与字段标签 DOM（原 tooltipExtraRichEditorDom 前半部分）
 */
import { ITooltipExtraItem } from '../types';
import { getFieldKey } from './dimensionAndOptions';

/** 零宽空格：换行后锚定光标；提交前由 normalizeEditorText 剥离 */
export const CARET_ANCHOR = '\u200b';

/** 从片段列表中只取 field 类型 */
export const getFieldItems = (items: ITooltipExtraItem[]): ITooltipExtraItem[] => {
  return items.filter((item) => item.type === 'field');
};

/** 提交前规范化文本：NBSP→空格、去掉 ZWSP、统一 CRLF */
export const normalizeEditorText = (text: string): string => {
  return text
    .replace(/\u00a0/g, ' ')
    .replace(new RegExp(CARET_ANCHOR, 'g'), '')
    .replace(/\r\n/g, '\n');
};

/** 将相邻多条 text 合并；field 原样保留 */
export const mergeTextItems = (items: ITooltipExtraItem[]): ITooltipExtraItem[] => {
  const result: ITooltipExtraItem[] = [];
  items.forEach((item) => {
    if (item.type === 'text') {
      const normalized = normalizeEditorText(String(item.value || ''));
      if (!normalized) {
        return;
      }
      const lastItem = result[result.length - 1];
      if (lastItem?.type === 'text') {
        lastItem.value = `${lastItem.value}${normalized}`;
      } else {
        result.push({
          type: 'text',
          value: normalized,
        });
      }
      return;
    }
    result.push(item);
  });
  return result;
};

/**
 * 无 field 且合并后仅空白/换行 → 视为空数组。
 * 避免删空编辑器后残留 br 被解析成 [{ text: "\\n" }] 仍算有内容。
 */
export const sanitizeMergedSubmitItems = (
  items: ITooltipExtraItem[],
): ITooltipExtraItem[] => {
  if (!items.length) {
    return [];
  }
  if (items.some((item) => item.type === 'field')) {
    return items;
  }
  if (!items.every((item) => item.type === 'text')) {
    return items;
  }
  const combined = items
    .map((item) => (item.type === 'text' ? String(item.value || '') : ''))
    .join('');
  if (/^\s*$/.test(normalizeEditorText(combined))) {
    return [];
  }
  return items;
};

/** 由 ITooltipExtraItem 生成可编辑区内的字段标签 DOM（含展示名与删除按钮） */
export const createFieldTagElement = (item: ITooltipExtraItem): HTMLSpanElement => {
  const label = item.label || item.value;
  const tag = document.createElement('span');
  tag.className = 'dbl-chart-tooltip-extra-field-tag';
  tag.contentEditable = 'false';
  tag.dataset.value = item.value;
  tag.dataset.label = label;
  tag.dataset.tableCode = item.tableCode || '';
  tag.dataset.column = item.column || '';
  tag.dataset.key = getFieldKey(item);

  const labelSpan = document.createElement('span');
  labelSpan.className = 'dbl-chart-tooltip-extra-field-tag-label';
  labelSpan.textContent = label;

  const removeButton = document.createElement('span');
  removeButton.className = 'dbl-chart-tooltip-extra-field-tag-remove';
  removeButton.dataset.role = 'remove-field-tag';
  removeButton.textContent = '×';

  tag.appendChild(labelSpan);
  tag.appendChild(removeButton);
  return tag;
};
